import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import Flex from "@/components/Flex";
import SideSheetDrawer from "@/components/SidesheetDrawer";
import PasswordInput from "@/components/app/PasswordInput";
import {
  CHANGE_PASSWORD_FORM_INITIAL_VALUES,
  ChangePasswordFormType,
} from "@/constants/setting-initial-values";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useChangePassword } from "@/queries/use-change-password";
import { getErrorSnackbar, getSuccessSnackbar } from "@/utils/utils";

interface PatientPortalChangePasswordProps {
  open: boolean;
  onClose: () => void;
}

const changePasswordValidationSchema = Yup.object({
  currentPassword: Yup.string().required().label("Current Password"),
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Must include at least one uppercase letter")
    .matches(/[a-z]/, "Must include at least one lowercase letter")
    .matches(/[0-9]/, "Must include at least one number")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "Must include at least one special character"
    )
    .notOneOf(
      [Yup.ref("currentPassword")],
      "New password must not match the current password"
    )
    .required()
    .label("New Password"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), ""], "Passwords must match")
    .required()
    .label("Confirm Password"),
});

const PASSWORD_RULES = [
  {
    label: "Minimum 8 characters long - the more, the better",
    test: (v: string) => v.length >= 8,
  },
  {
    label: "At least one lowercase character",
    test: (v: string) => /[a-z]/.test(v),
  },
  {
    label: "At least one Uppercase character",
    test: (v: string) => /[A-Z]/.test(v),
  },
  { label: "At least one Number", test: (v: string) => /[0-9]/.test(v) },
  {
    label: "At least one symbol",
    test: (v: string) => /[!@#$%^&*(),.?":{}|<>]/.test(v),
  },
];

const PatientPortalChangePassword = ({
  open,
  onClose,
}: PatientPortalChangePasswordProps) => {
  const { mutateAsync: changePassword } = useChangePassword();
  const showSnackbar = useSnackbar();

  const {
    values,
    handleBlur,
    touched,
    errors,
    handleSubmit,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: CHANGE_PASSWORD_FORM_INITIAL_VALUES,
    validationSchema: changePasswordValidationSchema,
    onSubmit,
  });

  function onSubmit(values: ChangePasswordFormType) {
    return changePassword({
      currentPassword: values.currentPassword.trim(),
      newPassword: values.newPassword.trim(),
    })
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        resetForm();
        onClose();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  }

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <SideSheetDrawer
      open={open}
      w="40vw"
      onClose={handleClose}
      title="Change Password"
    >
      <Flex flexDirection={"column"} gap={"18px"}>
        <Grid container spacing={2.5} direction={"column"}>
          <Grid item xs={12}>
            <PasswordInput
              name="currentPassword"
              onBlur={handleBlur}
              label={"Current Password"}
              placeholder="Current Password"
              value={values.currentPassword}
              onChange={(value) => {
                setFieldValue("currentPassword", value);
              }}
              required
              error={touched.currentPassword && !!errors.currentPassword}
            />
          </Grid>
          <Grid item xs={12}>
            <PasswordInput
              name="newPassword"
              onBlur={handleBlur}
              label={"New Password"}
              placeholder="New Password"
              value={values.newPassword}
              onChange={(value) => {
                setFieldValue("newPassword", value);
              }}
              required
              error={touched.newPassword && (errors.newPassword as any)}
            />
          </Grid>
          <Grid item xs={12}>
            <PasswordInput
              label={"Confirm Password"}
              placeholder="Confirm Password"
              name="confirmPassword"
              onBlur={handleBlur}
              value={values.confirmPassword}
              onChange={(value) => {
                setFieldValue("confirmPassword", value);
              }}
              required
              error={touched.confirmPassword && (errors.confirmPassword as any)}
            />
          </Grid>
        </Grid>

        <Button
          onClick={() => handleSubmit()}
          variant="contained"
          fullWidth
          sx={{
            mt: 1,
            py: 1.2,
            textTransform: "none",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          Change Password
        </Button>

        <Box display={"flex"} flexDirection={"column"} gap={1} mt={1}>
          {PASSWORD_RULES.map((rule) => {
            const passed = rule.test(values.newPassword);
            return (
              <Flex key={rule.label} alignItems={"center"} gap={1}>
                {passed ? (
                  <CheckIcon sx={{ fontSize: 18, color: "success.main" }} />
                ) : (
                  <CloseIcon sx={{ fontSize: 18, color: "primary.main" }} />
                )}
                <Typography
                  variant="body2"
                  color={passed ? "success.main" : "primary.main"}
                >
                  {rule.label}
                </Typography>
              </Flex>
            );
          })}
        </Box>
      </Flex>
    </SideSheetDrawer>
  );
};

export default PatientPortalChangePassword;
